// @ts-strict-ignore
import produce, {castDraft} from 'immer';

import {Cache} from './cache';
import {parseDate, ParseDateOptions} from './date';
import {defaultOrdering, identity, leqToNumericOrdering, lexicalCompose, mapArguments} from './function';

export enum OrderBy {
  Ascending = `asc`,
  Descending = `desc`,
}
function isOrderBy(val: any): val is OrderBy {
  return typeof val === `string` && (Object.values(OrderBy) as string[]).includes(val);
}
type OrderByObj = {number: OrderBy; range: OrderBy; date: OrderBy; base: OrderBy};

const verifyIndexRange = (array, index) => {
  if (index >= array.length || index < -array.length) {
    throw new Error(`IndexError: array index out of range`);
  }
};

/**
 * Perform a "splice" operation and return a new array without mutating the original
 * @param {Array} array - the original array to splice
 * @returns {Array} - a new array spliced from the original
 */
export function immutableSplice(array, ...args) {
  array = array.slice();
  array.splice(...args);
  return array;
}

/**
 * Remove an item by index and return the resulting array without mutating the original
 * @param {Array} array - the original array to remove an item from
 * @param {number} index - the index of the item to remove
 * @returns {Array} - a new array that is a copy of the original with an item removed
 */
export function removeByIndex(array, index) {
  verifyIndexRange(array, index);
  return immutableSplice(array, index, 1);
}

export function moveByIndex(array, index, newIndex) {
  verifyIndexRange(array, index);
  verifyIndexRange(array, newIndex);

  const arr = [...array];

  const temp = arr[index];

  arr[index] = arr[newIndex];
  arr[newIndex] = temp;

  return arr;
}

/**
 * Remove an item by value and return the resulting array without mutating the original
 * @param {Array} array - the original array to remove an item from
 * @param {*} value - the value to remove
 * @returns {Array} - a new array that is a copy of the original with an item removed
 */
export function removeByValue(array, value) {
  const index = array.indexOf(value);
  if (index === -1) {
    throw new Error(`ValueError: ${value} is not in array`);
  }
  return removeByIndex(array, index);
}

/**
 * Replace an item by index and return the resulting array without mutating the original
 * @param {Array} array - the original array to replace an item from
 * @param {number} index - the index of the item to replace
 * @param {*} value - the value to replace the item with
 * @returns {Array} - a new array that is a copy of the original with an item replaced
 */
export function replaceByIndex(array, index, value) {
  if (index >= array.length || index < -array.length) {
    throw new Error(`IndexError: array index out of range`);
  }
  return immutableSplice(array, index, 1, value);
}

/**
 * Insert an item at an index and return the resulting array without mutating the original
 * @param {Array} array - the original array to insert an item into
 * @param {number} index - the index to insert the new item at
 * @param {*} value - the value of the new item
 * @returns {Array} - a new array that is a copy of the original with the value inserted
 */
export function insertAtIndex(array, index, value) {
  if (index > array.length || index < -array.length) {
    throw new Error(`IndexError: array index out of range`);
  }
  return immutableSplice(array, index, 0, value);
}

/**
 * Adds a value to the end of an array or updates a value at a specific index.
 *
 * @template T - The type of elements in the array
 * @param {T[]} array - The source array
 * @param {number} index - The index at which to update the value
 * @param {T} value - The value to add or update
 * @returns {T[]} A new array with the value either added at the end (if index is invalid) or updated at the specified index
 *
 * @example
 * // Update an element
 * const arr = [1, 2, 3];
 * const updated = updateOrAddAtIndex(arr, 1, 5); // [1, 5, 3]
 *
 * @example
 * // Add a new element
 * const arr = [1, 2, 3];
 * const updated = updateOrAddAtIndex(arr, -1, 4); // [1, 2, 3, 4]
 */
export function updateOrAddAtIndex<T>(array: T[], index: number, value: T): T[] {
  if (index === -1 || index >= array.length) {
    return [...array, value];
  } else {
    return produce(array, (draft) => {
      draft[index] = castDraft(value);
    });
  }
}

// ensure null items (used to designate invalid items) are placed last in the sorted order
const NULL_ORDERING = leqToNumericOrdering((a, b) => a !== null || b === null);
export const SORT_ORDERING = {
  asc: lexicalCompose(NULL_ORDERING, (a, b) => defaultOrdering(a, b)),
  desc: lexicalCompose(NULL_ORDERING, (a, b) => defaultOrdering(b, a)),
};

type Transform<T, TR = T> = (arg: T) => TR;
type BaseComparatorConfig<T, TResult = T> = Partial<{
  order: OrderBy;
  transform: Transform<T, TResult>;
}>;
/**
 * Construct a sort comparator function that orders items by numeric value, comparing them via a <= operation.
 * null items are placed last in the sorted order.
 * @param {Object} options
 * @param {string} [options.order=`asc`] - `asc` (default) sorts ascending; `desc` sorts descending
 * @param {Function} [options.transform=identity] - a function that can transform each item before comparison (defaults to identity: x => x)
 * @returns {(...args: Array<any>) => any} - a sort comparator func that can be passed to Array.sort (for any given a and b returns one of [1, 0, -1])
 * @example
 * <<< [1, 2, 3, 4, 5].sort(baseComparator({order: `desc`, transform: n => n % 2}));
 * >>> [2, 4, 1, 3, 5] // sorted by even numbers followed by odd numbers
 */
export function baseComparator<T, TResult>({
  order = OrderBy.Ascending,
  transform,
}: BaseComparatorConfig<T, TResult> = {}) {
  return mapArguments(SORT_ORDERING[order], transform ?? identity);
}

/**
 * Construct a sort comparator function that orders items by a given condition, placing items for which the condition is true first.
 * null items are placed last in the sorted order.
 * @param {Function} [condition=identity] - a function that returns a true or false value for each item (defaults to identity: x => x)
 * @returns {SortingFunction} - a sort comparator func that can be passed to Array.sort (for any given a and b returns one of [1, 0, -1])
 * @example
 * <<< [1, 2, 3, 4, 5].sort(boolComparator(num => num > 3));
 * >>> [4, 5, 1, 2, 3] // numbers greater than 3 placed first in the order
 */
export function boolComparator(condition = identity) {
  const boolCondition = (x) => Boolean(condition(x)); // prevent subtle ordering bugs cause by forgetting to cast to bool in the condition
  return mapArguments(SORT_ORDERING[`desc`], boolCondition); // sort order is `desc` so "true" (1) items are placed before "false" (0) items
}

/**
 * Construct a sort comparator function that orders date strings by parsed date value, converting them to integer timestamps and
 * then comparing via a <= operation. Invalid date strings are placed last in the sorted order.
 * @param {Object} options
 * @param {string} [options.order=`asc`] - `asc` (default) sorts ascending; `desc` sorts descending
 * @param {Function} [options.transform=identity] - a function that can transform each item before comparison (defaults to identity: x => x)
 * @param {Cache} [options.parseDateCache] - a Cache instance used to store date strings <-> timestamps to avoid redundant parsing
 * @param {object} [options.parseDateConfig] - a config object that provides fine-grained control over how date strings are parsed (see parseDate)
 * @param {string|RegExp} [options.dateRegex] - a regex that can be used to only try and parse certain strings as dates (for performance)
 * @returns {(...args: Array<any>) => any} - a sort comparator func that can be passed to Array.sort (for any given a and b returns one of [1, 0, -1])
 * @example
 * <<< [`12~1`, `blarg`, `12~2`].sort(dateStringComparator({order: `desc`, transform: dateString => dateString.replace(`~`, ` `)}));
 * >>> [`12~2`, `12~1`, `blarg`] // sorted by parseable date value
 *
 * If you want to cache parsed date values across multiple sorts for performance, initialize your own parseDateCache externally
 * and pass in that single instance:
 *
 *   const parseDateCache = new Cache();
 * ... later, within your sorting code ...
 *   array.sort(dateStringComparator({parseDateCache}));
 */
export function dateStringComparator({
  order = OrderBy.Ascending,
  transform = identity,
  parseDateCache = null,
  parseDateConfig = {},
  dateRegex = null,
}: Partial<{
  order: OrderBy;
  transform: TODO;
  parseDateCache: Nullable<Cache>;
  parseDateConfig: ParseDateOptions;
  dateRegex: Nullable<string | RegExp>;
}> = {}) {
  const cache = parseDateCache || new Cache();
  dateRegex = dateRegex && new RegExp(dateRegex);

  return mapArguments(SORT_ORDERING[order], (item) => {
    item = transform(item);
    return cache.fetch(item, (cacheItem) => {
      let timestamp: Nullable<number> = null;
      if (!dateRegex || (typeof dateRegex === `object` && dateRegex.test(cacheItem))) {
        const date = parseDate(cacheItem, parseDateConfig);
        timestamp = date && date.getTime();
      }
      return timestamp;
    });
  });
}

/**
 * Construct a sort comparator function that orders numeric strings by parsed numeric value, converting them to Numbers and
 * then comparing via a <= operation. Invalid numeric strings are placed last in the sorted order.
 * @param {object} options
 * @param {string} [options.order=`asc`] - `asc` (default) sorts ascending; `desc` sorts descending
 * @param {Function} [options.transform=identity] - a function that can transform each item before comparison (defaults to identity: x => x)
 * @param {Cache} [options.parseNumberCache] - a Cache instance used to store number-like values <-> numbers to avoid redundant parsing
 * @returns {(a: any, b: any) => number} - a sort comparator func that can be passed to Array.sort (for any given a and b returns one of [1, 0, -1])
 * @example
 * <<< [`n:-9,000`, `n:blarg`, `n:42`].sort(numericComparator({order: `desc`, transform: numericString => numericString.split(:)[1]}));
 * >>> [`n:42`, `n:-9,000`, `n:blarg`] // sorted by parseable numeric value
 *
 * If you want to cache parsed number values across multiple sorts for performance, initialize your own parseNumberCache externally
 * and pass in that single instance:
 *
 *   const parseNumberCache = new Cache();
 * ... later, within your sorting code ...
 *   array.sort(dateStringComparator({parseNumberCache}));
 */
export function numericComparator({
  order = OrderBy.Ascending,
  transform = identity,
  parseNumberCache = null,
}: Partial<{
  order: OrderBy;
  transform: TODO;
  parseNumberCache: Nullable<Cache>;
}> = {}) {
  const cache = parseNumberCache || new Cache();

  return mapArguments(SORT_ORDERING[order], (item) => {
    item = transform(item);

    return cache.fetch(item, (cacheItem) => {
      const number = Number(cacheItem && cacheItem.replace ? cacheItem.replace(/,/g, ``) : cacheItem);
      return !isNaN(number) ? number : null;
    });
  });
}

/**
 * Construct a sort comparator function that orders range strings by parsed numeric value of the first number in the range, converting them to Numbers and
 * then comparing via a <= operation. Invalid range strings are placed last in the sorted order.
 * @param {string} [order=`asc`] - `asc` (default) sorts ascending; `desc` sorts descending
 * @param {Cache} [parseRangeCache] - a Cache instance used to store range-like values <-> numbers to avoid redundant parsing
 * @returns {Function} - a sort comparator func that can be passed to Array.sort (for any given a and b returns one of [1, 0, -1])
 * @example
 * <<< [`10-20`, `5-10`, `20-30`].sort(rangeComparator({order: `desc`}));
 * >>> [`20-30`, `10-20`, `5-10`] // sorted by first element in range
 *
 * If you want to cache parsed number values across multiple sorts for performance, initialize your own parseRangeCache externally
 * and pass in that single instance:
 *
 *   const parseRangeCache = new Cache();
 * ... later, within your sorting code ...
 *   array.sort(rangeComparator({parseRangeCache}));
 */
export function rangeComparator({
  order = OrderBy.Ascending,
  transform = identity,
  parseRangeCache = null,
  separator = `-`,
}: Partial<{order: OrderBy; transform: TODO; parseRangeCache: Nullable<Cache>; separator: string}> = {}) {
  const cache = parseRangeCache || new Cache();
  return mapArguments(SORT_ORDERING[order], (item) => {
    /**
     * Matches the following:
     * 10-20
     * 10 - 20
     * 10.0 - 20.0
     */
    const number = `\\d+\\.?\\d*`;
    const spaces = `\\s*`;
    const rangeRegex = new RegExp(`^${number}${spaces}${separator}${spaces}${number}$`);
    return cache.fetch(item, (cacheItem) => {
      cacheItem = transform(cacheItem);
      let firstNum: Nullable<number> = null;
      if (rangeRegex.test(cacheItem)) {
        // Grabs first number of the range
        const spacesOrSeparatorRegex = new RegExp(`(\\s|${separator})`);
        firstNum = Number(cacheItem.split(spacesOrSeparatorRegex)[0]);
      }
      return firstNum;
    });
  });
}

/**
 * Construct a sort comparator function that orders strings by parsed date, numeric, or alphabetic value.
 * @param {string|object} [order=`asc`] - `asc`/`desc` order string. Can also be an object like {base: `asc`, number: `desc`, date: `desc`} for finer-grained control over sub-comparators
 * @param {Function} [transform=identity] - a function that can transform each item before comparison (defaults to identity: x => x)
 * @param {Cache} [parseNumberCache] - a Cache instance used to store number-like values <-> numbers to avoid redundant parsing
 * @param {Cache} [parseDateCache] - a Cache instance used to store date strings <-> timestamps to avoid redundant parsing
 * @param {object} [parseDateConfig] - a config object that provides fine-grained control over how date strings are parsed (see parseDate)
 * @param {string|RegExp} [dateRegex] - a regex that can be used to only try and parse certain strings as dates (for performance)
 * @returns {Function} - a sort comparator func that can be passed to Array.sort (for any given a and b returns one of [1, 0, -1])
 * @example
 * <<< [`12/1`, `blarg`, `-9,000`, `12/2`, `42`].sort(baseComparator({order: `desc`}));
 * >>> [`42`, `-9,000`, `12/2`, `12/1`, `blarg`]
 */
export function numDateAlphaComparator({
  order = OrderBy.Ascending,
  transform = identity,
  parseNumberCache = null,
  parseRangeCache = null,
  parseDateCache = null,
  parseDateConfig = {},
  dateRegex = null,
}: {
  order?: OrderBy | Partial<OrderByObj>;
  transform?: TODO;
  parseNumberCache?: Nullable<Cache>;
  parseRangeCache?: Nullable<Cache>;
  parseDateCache?: Nullable<Cache>;
  parseDateConfig?: TODO;
  dateRegex?: Nullable<string | RegExp>;
} = {}) {
  const orderedBy: OrderByObj = isOrderBy(order)
    ? {
        number: order,
        range: order,
        date: order,
        base: order,
      }
    : {
        number: (order && order.number) || OrderBy.Ascending,
        range: (order && order.range) || OrderBy.Ascending,
        date: (order && order.date) || OrderBy.Ascending,
        base: (order && order.base) || OrderBy.Ascending,
      };

  return lexicalCompose(
    numericComparator({order: orderedBy.number, transform, parseNumberCache}),
    rangeComparator({order: orderedBy.range, transform, parseRangeCache}),
    dateStringComparator({order: orderedBy.date, transform, parseDateCache, parseDateConfig, dateRegex}),
    baseComparator({order: orderedBy.base, transform}),
  );
}

/**
 * Comparator for sorting report headers in Insights and Dashboard.
 *
 * Will attempt to parse and sort values as number, date, or string.
 * String sorting is case-insensitive.
 * Treats date strings like "Jun 10 - Jun 16" as "Jun 10".
 * Sorts dates in reverse order (e.g. ascending means latest date first).
 */
export function headerComparator({order = OrderBy.Ascending, transform = identity} = {}) {
  const lowercase = (item) => (typeof item === `string` ? item.toLowerCase() : item);
  return numDateAlphaComparator({
    order: {
      base: order,
      number: order,
      range: order,
      date: order === OrderBy.Ascending ? OrderBy.Descending : OrderBy.Ascending,
    },
    transform: (item) => lowercase(transform(item)),
    parseNumberCache: new Cache(),
    parseRangeCache: new Cache(),
    parseDateCache: new Cache(),
  });
}

/**
 * Sort array without mutating original
 * @param {Array} arr - array to sort
 * @param {Object} options
 * @param {string} [options.order='asc'] - sort order ('asc' or 'desc')
 * @param {function} [options.transform=null] - transforms each element before comparison
 * @example
 * sorted(['XX', 'A', 'g'], {transform: s => s.toLowerCase()});
 * // ['A', 'g', 'XX']
 */
export function sorted<T, TResult = T>(
  arr: T[],
  {order = OrderBy.Ascending, transform}: BaseComparatorConfig<T, TResult> = {},
) {
  return arr.slice().sort(baseComparator<T, TResult>(transform ? {order, transform} : {order}));
}

/**
 * Return a copy of an array with duplicate items removed
 * @param {Array} arr - array to de-duplicate
 * @param {function} [options.hash] - hashes each element for comparison
 * Items are compared via strict equality so duplicate arrays/objects will not be removed
 * unless a `hash` function is specified to hash objects before comparison.
 * @example
 * unique([{'a': 1, 'b': 2}, {'a': 1, 'b': 3}, {'a': 1, 'b': 2}], {hash: o => `${o['a']}:${o['b']}`});
 * // [{'a': 1, 'b': 2}, {'a': 1, 'b': 3}]
 */
export function unique<T>(arr: T[], options: {hash?: (item: T) => string} = {}) {
  let hashed;
  const encountered = new Set();
  return arr.filter((item) => {
    hashed = options.hash ? options.hash(item) : item;
    return encountered.has(hashed) ? false : encountered.add(hashed);
  });
}

/**
 * If the value is an array, return it. Otherwise, return the default value.
 *
 * @param {Array} value - the value to check
 * @param {Array} defaultValue - the default value to return if the value is not an array
 * @example
 * ```ts
 * let val = arrayOrDefault([1, 2, 3], [4, 5, 6]); // val = [1, 2, 3]
 * val = arrayOrDefault(null, [4, 5, 6]); // val = [4, 5, 6]
 * val = arrayOrDefault(undefined, [4, 5, 6]); // val = [4, 5, 6]
 * val = arrayOrDefault({ 'not': 'an array' }, [4, 5, 6]); // val = [4, 5, 6]
 * ```
 */
export function arrayOrDefault<T>(value: T[] | undefined | null, defaultValue: T[] = []) {
  if (!value || !Array.isArray(value)) {
    return defaultValue;
  }
  return value;
}
